import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { Badge, Card, EmptyState, LoadingBlock, type BadgeTone } from '@/design'
import { ApiError } from '@/lib/api'

import { domainsBackendFor } from './api'
import { STATUS_LABELS, needsDns, type DnsRecord, type DomainCheck, type DomainState } from './types'
import './domains.css'

/**
 * Settings › Domains, for Owner and Administrator only (`settings.manage`).
 *
 * The page is a two-step instruction sheet rather than a form: a customer adds
 * a hostname, goes to their own DNS console, and comes back to press Verify.
 * So the two records are shown as values to copy — not described — and a check
 * that has not passed says *which* record is still missing, because "not
 * verified" is the one message that cannot be acted on.
 */
export function DomainsPage() {
  const client = useApiClient()
  const backend = domainsBackendFor(client)
  const queryClient = useQueryClient()

  const state = useQuery({
    queryKey: ['tenant-domain', client.kind],
    queryFn: () => backend.state(),
  })

  /** Every mutation ends the same way: ask the server again, never patch locally. */
  const refresh = (next: DomainState) => {
    queryClient.setQueryData(['tenant-domain', client.kind], next)
  }

  const add = useMutation({
    mutationFn: (hostname: string) => backend.request(hostname),
    onSuccess: refresh,
  })
  const verify = useMutation({ mutationFn: () => backend.verify(), onSuccess: refresh })
  const remove = useMutation({ mutationFn: () => backend.remove(), onSuccess: refresh })

  if (state.isPending) return <LoadingBlock rows={4} label="Loading the custom domain" />
  if (state.isError) return <LoadError error={state.error} />

  const { platform_host: platformHost, domain, check } = state.data
  // `check` comes from the stored state and nowhere else. Every mutation writes
  // its own response there, so adding a different hostname clears it — reading
  // `verify.data` instead would show the last check's result against a hostname
  // it was never run on.
  const busy = add.isPending || verify.isPending || remove.isPending

  return (
    <div className="ci-domains">
      <Card
        title="Custom domain"
        subtitle="The address your team uses to reach Competition Insights."
      >
        {domain && domain.status !== 'removed' ? (
          <div className="ci-domains__current">
            <span className="ci-domains__hostname">
              <strong>{domain.hostname}</strong>
              <Badge tone={statusTone(domain.status)}>{STATUS_LABELS[domain.status]}</Badge>
            </span>
            <p className="ci-domains__note">{statusNote(domain.status, platformHost)}</p>
            <span className="ci-domains__actions">
              {needsDns(domain) ? (
                <button
                  type="button"
                  className="ci-button"
                  data-variant="primary"
                  disabled={busy}
                  onClick={() => verify.mutate()}
                >
                  {verify.isPending ? 'Checking DNS…' : 'Verify'}
                </button>
              ) : null}
              <button
                type="button"
                className="ci-button"
                data-variant="ghost"
                disabled={busy}
                onClick={() => {
                  const message =
                    domain.status === 'active'
                      ? `Remove ${domain.hostname}? Your team will have to use ${platformHost} again, and the address stops working immediately.`
                      : `Remove the request for ${domain.hostname}?`
                  if (window.confirm(message)) remove.mutate()
                }}
              >
                Remove
              </button>
            </span>
            <ApiMessage error={verify.error ?? remove.error} />
            {check && needsDns(domain) ? <CheckResult check={check} /> : null}
          </div>
        ) : (
          <EmptyState
            variant="no-data"
            title="No custom domain yet"
            description={`Competition Insights is served at ${platformHost}. Add a hostname below to use your own instead.`}
          />
        )}
      </Card>

      {domain && needsDns(domain) ? (
        <Card
          title="Add these two records to your DNS"
          subtitle="Your domain provider calls this DNS, Zone editor or Nameserver settings."
        >
          <ol className="ci-domains__steps">
            {domain.records.map((record) => (
              <li key={record.type}>
                <RecordRow record={record} />
              </li>
            ))}
          </ol>
          <p className="ci-domains__note">
            Records usually appear within a few minutes, but your provider may take up to 24 hours.
            Press <strong>Verify</strong> when they are published — nothing breaks if you press it early.
            The certificate is issued automatically once the domain is verified, and renews itself.
          </p>
        </Card>
      ) : null}

      {!domain || domain.status === 'removed' || domain.status === 'active' ? (
        <AddForm
          busy={add.isPending}
          error={add.error}
          disabled={domain?.status === 'active'}
          platformHost={platformHost}
          liveHostname={domain?.status === 'active' ? domain.hostname : null}
          onAdd={(hostname) => add.mutate(hostname)}
        />
      ) : null}
    </div>
  )
}

function RecordRow({ record }: { record: DnsRecord }) {
  return (
    <div className="ci-domains__record">
      <span>
        <small>Type</small>
        <code>{record.type}</code>
      </span>
      <span>
        <small>{record.type === 'TXT' ? 'Name' : 'Host'}</small>
        <code>{record.name}</code>
      </span>
      <span>
        <small>{record.type === 'TXT' ? 'Value' : 'Points to'}</small>
        <code>{record.value}</code>
      </span>
    </div>
  )
}

/** What the last check saw — so the next step is always a specific one. */
function CheckResult({ check }: { check: DomainCheck }) {
  if (check.verified) {
    return (
      <p role="status" className="ci-domains__ok">
        Both records are published. Your domain is live.
      </p>
    )
  }
  return (
    <ul role="status" className="ci-domains__check">
      <li>
        {check.points_at_platform
          ? 'The CNAME record is published.'
          : check.observed_addresses.length > 0
            ? 'The CNAME record points somewhere else. Check it against the value above.'
            : 'The CNAME record is not visible yet.'}
      </li>
      <li>
        {check.token_published
          ? 'The TXT record is published.'
          : 'The TXT record is not visible yet. Its value has to match exactly.'}
      </li>
    </ul>
  )
}

function AddForm({
  busy,
  error,
  disabled,
  platformHost,
  liveHostname,
  onAdd,
}: {
  busy: boolean
  error: unknown
  disabled: boolean
  platformHost: string
  liveHostname: string | null
  onAdd: (hostname: string) => void
}) {
  const [hostname, setHostname] = useState('')

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!hostname.trim()) return
    onAdd(hostname.trim())
    setHostname('')
  }

  return (
    <Card
      title={liveHostname ? 'Change your custom domain' : 'Add a custom domain'}
      subtitle={`A subdomain of a domain you own, such as insights.your-company.co.uk. It will point at ${platformHost}.`}
    >
      {liveHostname ? (
        <p className="ci-domains__note">
          Remove {liveHostname} first. One custom domain is live at a time, so changing it is a
          deliberate step rather than something that happens while you add the next one.
        </p>
      ) : null}
      <form className="ci-domains__add" aria-label="Add a custom domain" onSubmit={submit}>
        <label>
          Hostname
          <input
            type="text"
            name="hostname"
            required
            autoComplete="off"
            spellCheck={false}
            placeholder="insights.your-company.co.uk"
            disabled={disabled || busy}
            value={hostname}
            onChange={(event) => setHostname(event.target.value)}
          />
        </label>
        <button type="submit" className="ci-button" data-variant="primary" disabled={disabled || busy}>
          {busy ? 'Adding…' : 'Add domain'}
        </button>
      </form>
      <ApiMessage error={error} />
    </Card>
  )
}

/** Whatever the API said went wrong, in its own words. */
function ApiMessage({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof ApiError ? error.message : 'Something went wrong. Try again shortly.'
  return (
    <p role="alert" className="ci-domains__error">
      {message}
    </p>
  )
}

function LoadError({ error }: { error: unknown }) {
  const refused = error instanceof ApiError && error.status === 403
  return (
    <EmptyState
      variant={refused ? 'permission' : 'outage'}
      description={
        refused
          ? 'You need Owner or Administrator access to manage the custom domain.'
          : 'Could not load the custom domain. Try again shortly.'
      }
    />
  )
}

function statusTone(status: string): BadgeTone {
  if (status === 'active') return 'positive'
  if (status === 'verified') return 'accent'
  return 'warning'
}

function statusNote(status: string, platformHost: string): string {
  switch (status) {
    case 'active':
      return `Your team can sign in here. ${platformHost} keeps working too.`
    case 'verified':
      return 'Both records checked out. Finishing setup.'
    default:
      return `Not live yet — keep using ${platformHost} until the two records below are published.`
  }
}
